'use client';

import React, { useState } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { QuickCalculatorForm } from '@/components/quick-calculator/QuickCalculatorForm';
import { calculateAscendant, QuickFactAscendant, CreateBirthProfileInput } from '@/lib/api/client';
import { Compass } from 'lucide-react';

export interface AscendantCalculatorClientProps {
  initialResult?: QuickFactAscendant | null;
  initialError?: string | null;
}

export function AscendantCalculatorClient({
  initialResult = null,
  initialError = null
}: AscendantCalculatorClientProps) {
  const t = useTranslations('ascendant');
  const tGlossary = useTranslations('glossary');
  const locale = useLocale();

  const [result, setResult] = useState<QuickFactAscendant | null>(initialResult);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(initialError);

  const handleCalculate = async (input: CreateBirthProfileInput) => {
    setLoading(true);
    setError(null);
    try {
      const data = await calculateAscendant(input);
      setResult(data);
    } catch (err: any) {
      setError(err.message || 'Failed to calculate Ascendant');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const getSignName = (signName?: string, sanskritName?: string): string => {
    if (!signName) return '';
    if (locale === 'hi') {
      try {
        const val = tGlossary(`zodiacSigns.${signName}`);
        if (val && !val.startsWith('zodiacSigns.')) return val;
      } catch {}
      return signName;
    }
    return sanskritName ? `${signName} (${sanskritName})` : signName;
  };

  const getNakshatraName = (nakName?: string): string => {
    if (!nakName) return '';
    const nakMap: Record<string, string> = {
      'Ashwini': '1', 'Bharani': '2', 'Krittika': '3', 'Rohini': '4',
      'Mrigashira': '5', 'Ardra': '6', 'Punarvasu': '7', 'Pushya': '8',
      'Ashlesha': '9', 'Magha': '10', 'Purva Phalguni': '11', 'Uttara Phalguni': '12',
      'Hasta': '13', 'Chitra': '14', 'Swati': '15', 'Vishakha': '16',
      'Anuradha': '17', 'Jyeshtha': '18', 'Mula': '19', 'Purva Ashadha': '20',
      'Uttara Ashadha': '21', 'Shravana': '22', 'Dhanishta': '23', 'Shatabhisha': '24',
      'Purva Bhadrapada': '25', 'Uttara Bhadrapada': '26', 'Revati': '27'
    };
    const id = nakMap[nakName];
    if (id) {
      try {
        const val = tGlossary(`nakshatras.${id}`);
        if (val && !val.startsWith('nakshatras.')) return val;
      } catch {}
    }
    return nakName;
  };

  const getPadaDisplay = (pada: number): string => {
    try {
      const val = tGlossary(`padas.${pada}`);
      if (val && !val.startsWith('padas.')) return val;
    } catch {}
    return `Pada ${pada}`;
  };

  return (
    <div className="space-y-8">
      <QuickCalculatorForm
        title={t('title')}
        subtitle={t('subtitle')}
        onCalculate={handleCalculate}
        loading={loading}
      />

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400 text-xs text-center font-mono">
          {error}
        </div>
      )}

      {result && (
        <div
          className="bg-[var(--card)] border border-[var(--line)] rounded-2xl p-6 sm:p-8 shadow-md space-y-6 text-center sm:text-left"
          data-testid="ascendant-result"
        >
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <div className="w-20 h-20 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-500">
              <Compass className="w-10 h-10" />
            </div>
            <div className="space-y-1">
              <div className="text-[10px] font-bold text-emerald-500 uppercase tracking-wider mb-1">
                {t('resultBadge')}
              </div>
              <h2 className="text-3xl font-serif font-bold text-[var(--foreground)]">
                {getSignName(result.sign_name, result.sanskrit_sign_name)}
              </h2>
              <p className="text-xs font-mono text-[var(--gold)]">
                {t('degreeInSign', {
                  formatted: result.formatted_degree,
                  degrees: result.degree_in_sign.toFixed(2)
                })}
              </p>
              <p className="text-xs text-[var(--muted)] pt-1">
                {t('ascendantNakshatra', {
                  nakshatra: getNakshatraName(result.nakshatra_name),
                  pada: getPadaDisplay(result.nakshatra_pada)
                })}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
